import React, { useEffect, useMemo, useState, useContext } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { LoaderContext } from "../Components/dashboard";
import axios from "axios";
import { URL as BASE_URL } from "../config";

const themeColor = "#ff002b";

const HomeBannerList = () => {
  const { setLoading } = useContext(LoaderContext);
  const [tableLoading, setTableLoading] = useState(false);

  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const navigate = useNavigate();

  const fetchBanners = async () => {
    const startTime = Date.now();
    setTableLoading(true);

    try {
      const res = await axios.get(`${BASE_URL}/homebanner`);
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setDataSource(data);
    } catch (err) {
      console.error("Failed to fetch banners:", err);
      setDataSource([]);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;

      setTimeout(() => {
        setTableLoading(false);
      }, remaining > 0 ? remaining : 0);
    }
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    fetchBanners().finally(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);


  const handleEdit = (id) => navigate(`/dashboard/homebanner/${id}`);
  const handleAddNew = () => navigate(`/dashboard/homebanner`);

  const columns = useMemo(
    () => [
      {
        accessorKey: "actions",
        header: "Action",
        size: 35,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() => handleEdit(row.original.homeBannerId)}
            sx={{
              color: themeColor,
              backgroundColor: "#ffe6e9",
              "&:hover": { backgroundColor: "#ffe6e9" },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      { accessorKey: "homeBannerId", header: "ID", size: 50 },
      {
        accessorKey: "homeBannerImg",
        header: "Banner Image",
        size: 120,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <img
              src={`${BASE_URL}${cell.getValue()}`}
              alt="banner"
              style={{
                width: 70,
                height: 40,
                objectFit: "cover",
                borderRadius: 6,
                border: "1px solid #ddd",
              }}
            />
          ) : (
            "-"
          ),
      },
    ],
    []
  );

  const filteredData = useMemo(() => {
    return [...dataSource]
      .reverse()
      .filter((item) =>
        Object.values(item || {})
          .join(" ")
          .toLowerCase()
          .includes(globalFilter.toLowerCase())
      );
  }, [dataSource, globalFilter]);

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper elevation={4} sx={{ p: 2, borderRadius: 3 }}>
      
        <Box display="flex" justifyContent="center" mt={1.5} mb={1.8}>
          <Box
            sx={{
              background: `linear-gradient(90deg, ${themeColor}, #ff6a6a)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
              }}
            >
              Home Banners
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: 2.2,
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }}
        />

      
        <Stack
  direction={{ xs: "column", sm: "row" }}
  spacing={1.2}
  alignItems="center"
  justifyContent={
    dataSource.length === 0 ? "space-between" : "flex-end"
  }
  sx={{ mb: 2.5 }}
>

  {dataSource.length === 0 && (
    <Button
      size="small"
      variant="contained"
      onClick={handleAddNew}
      sx={{
        borderRadius: 20,
        px: 2.2,
        minHeight: 32,
        textTransform: "none",
        backgroundColor: themeColor,
        color: "#fff",
      }}
    >
      Add New Banner
    </Button>
  )}


  <TextField
    size="small"
    placeholder="Search banner..."
    value={globalFilter}
    onChange={(e) => setGlobalFilter(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ color: themeColor, fontSize: 18 }} />
        </InputAdornment>
      ),
    }}
    sx={{
      width: { xs: "100%", sm: 240 },
      "& .MuiOutlinedInput-root": {
        borderRadius: 20,
        height: 34,
        "& fieldset": { borderColor: "#454444" },
        "&:hover fieldset": {
          borderColor: themeColor,
          boxShadow: `0 0 0 2px ${themeColor}22`,
        },
        "&.Mui-focused fieldset": {
          borderColor: themeColor,
          boxShadow: `0 0 0 3px ${themeColor}33`,
        },
      },
    }}
  />
</Stack>

         <MaterialReactTable
                                 columns={columns}
                                 data={filteredData}
                                 state={{ isLoading: tableLoading }}
                              
                                 enableTopToolbar={false}
                                 enableColumnActions={false}
                                 enableDensityToggle={false}
                                 enableFullScreenToggle={false}
                                 enableColumnFilters={false}
                                 enableGlobalFilter={false}
                                 enableHiding={false}
                               
                              
                                 muiTablePaperProps={{
                                   sx: {
                                     boxShadow: "none",
                                     mt: 0.5,
                                   },
                                 }}
                               
                              
                                 muiTableContainerProps={{
                                   sx: {
                                     maxHeight: { xs: "55vh", sm: "none" },
                                     "&::-webkit-scrollbar": {
                                       height: "6px",
                                     },
                                     "&::-webkit-scrollbar-track": {
                                       background: "transparent",
                                     },
                                     "&::-webkit-scrollbar-thumb": {
                                       backgroundColor: "#cfcfcf",
                                       borderRadius: "4px",
                                     },
                                     "&::-webkit-scrollbar-thumb:hover": {
                                       backgroundColor: "#b0b0b0",
                                     },
                                   },
                                 }}
                               
                              
                                 muiTableHeadCellProps={{
                                   sx: {
                                     backgroundColor: "#ffe6e9",
                                     fontWeight: 700,
                                     fontSize: { xs: "12px", sm: "14px" },
                                     py: { xs: 0.8, sm: 1.2 },
                                     whiteSpace: "nowrap",
                                   },
                                 }}
                               
                                
                                 muiTableBodyRowProps={{
                                   sx: {
                                     "& td": {
                                       fontSize: { xs: "11px", sm: "14px" },
                                       py: { xs: 0.5, sm: 1 },
                                       whiteSpace: "nowrap",
                                     },
                                     "&:hover": {
                                       backgroundColor: "#fff8e1",
                                     },
                                   },
                                 }}
                               
                              
                                 initialState={{
                                   pagination: {
                                     pageSize: 50,
                                     pageIndex: 0,
                                   },
                                 }}
                               />
      </Paper>
    </Box>
  );
};

export default HomeBannerList;
